import { useState } from "react";
import { useFetch, PageHeader, Loading, ErrorState, EmptyState, SectionCard, Paginator, fmtDate } from "@/lib/ui";
import { useSelection, RowCheck, SelectAllCheck, DataActionDialog } from "@/lib/bulk";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ENTITIES = [["orders", "Orders"], ["users", "Customers & users"], ["shops", "Shops"], ["products", "Products"],
  ["categories", "Categories"], ["coupons", "Coupons"], ["messages", "Shop messages"]];

function AuditList() {
  const { data, loading } = useFetch("/admin/data-audit?limit=20", []);
  if (loading) return <Loading />;
  return (
    <SectionCard className="p-4 mt-6" data-testid="data-audit">
      <h3 className="font-semibold font-outfit mb-2">Archive &amp; restore audit log</h3>
      {!data?.length ? <p className="text-sm text-muted-foreground">No actions yet.</p> : data.map((a) => (
        <div key={a.id} className="text-xs border-t py-1.5" data-testid={`data-audit-${a.id}`}>
          <span className="font-medium">{a.action.replace("data_", "")}</span> · {a.entity} · {a.count ?? "—"} record(s) · by {a.by} · {fmtDate(a.at)}
        </div>))}
    </SectionCard>
  );
}

export default function DataManagement() {
  const [entity, setEntity] = useState("orders");
  const [archived, setArchived] = useState(false);
  const [search, setSearch] = useState(""); const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const path = `/admin/data/${entity}?archived=${archived}&page=${page}&limit=50${search ? `&search=${encodeURIComponent(search)}` : ""}`;
  const { data, loading, error, refetch } = useFetch(path, [path]);
  const sel = useSelection(data?.items);
  const reset = (fn) => (v) => { fn(v); setPage(1); sel.clear(); };
  return (
    <div>
      <PageHeader title="Data Management" subtitle="Owner-only. Records are archived, never erased; payments, payouts, refunds, GST and audit logs cannot be removed." />
      <div className="flex flex-wrap gap-2 mb-3">
        <select className="border rounded px-2 py-1.5 text-sm bg-background" value={entity} onChange={(e) => reset(setEntity)(e.target.value)} data-testid="data-entity-select">
          {ENTITIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Tabs value={archived ? "archived" : "active"} onValueChange={(v) => reset(setArchived)(v === "archived")}>
          <TabsList><TabsTrigger value="active" data-testid="data-tab-active">Active</TabsTrigger><TabsTrigger value="archived" data-testid="data-tab-archived">Archived</TabsTrigger></TabsList>
        </Tabs>
        <Input className="w-56" placeholder="Search (e.g. TEST)" value={search} onChange={(e) => reset(setSearch)(e.target.value)} data-testid="data-search" />
        <Button className="ml-auto" variant={archived ? "default" : "destructive"} disabled={!sel.count} onClick={() => setOpen(true)} data-testid="data-action-btn">
          {archived ? "Restore" : "Archive"} selected ({sel.count})
        </Button>
      </div>
      {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> : !data.items.length ? <SectionCard className="p-2"><EmptyState label="No records" /></SectionCard> : (
        <SectionCard className="p-2 overflow-x-auto">
          <table className="w-full text-sm" data-testid="data-table">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="p-2"><SelectAllCheck checked={sel.allChecked} onCheck={sel.toggleAll} /></th><th>Record</th><th>Details</th><th>Created</th>{archived && <th>Archived</th>}</tr></thead>
            <tbody>{data.items.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`data-row-${r.id}`}>
                <td className="p-2"><RowCheck checked={sel.selected.has(r.id)} onCheck={() => sel.toggle(r.id)} testid={`data-check-${r.id}`} /></td>
                <td className="font-medium">{r.label || r.id}</td><td className="text-xs text-muted-foreground">{r.detail}</td>
                <td className="text-xs">{fmtDate(r.created_at)}</td>{archived && <td className="text-xs">{fmtDate(r.deleted_at)}</td>}
              </tr>))}</tbody>
          </table>
          <Paginator page={page} total={data.total} limit={50} onPage={setPage} />
        </SectionCard>)}
      <DataActionDialog entity={entity} ids={sel.ids} action={archived ? "restore" : "archive"} open={open}
        onClose={() => setOpen(false)} onDone={() => { sel.clear(); refetch(); }} />
      <AuditList />
    </div>
  );
}
